import React, { useState, useEffect } from 'react';
import AuthorFields from '../components/AuthorFields';
import CitationResult from '../components/CitationResult';
import { formatAuthorsForCitation, formatAuthorsForParenthetical, formatAuthorsForReference, formatURL } from '../utils/citationHelpers';

function WebsiteForm() {
  const [authors, setAuthors] = useState([]);
  const [authorType, setAuthorType] = useState('personal');
  const [formData, setFormData] = useState({
    title: '',
    websiteName: '',
    year: '',
    day: '',
    month: '',
    url: ''
  });
  const [result, setResult] = useState(null);
  const [liveResult, setLiveResult] = useState(null);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Genera una vista previa en tiempo real
  useEffect(() => {
    if (authors.length > 0 && formData.title && (formData.year || formData.year === 's.f.' || formData.year === 's. f.')) {
      generatePreview();
    } else {
      setLiveResult(null);
    }
  }, [authors, formData]);

  const formatDate = () => {
    const { year, month, day } = formData;
    if (!year) return '';

    // Manejar casos de "sin fecha"
    if (year === 's.f.' || year === 's. f.') {
      return year;
    }

    if (day && month) {
      const monthNames = {
        '01': 'enero', '02': 'febrero', '03': 'marzo', '04': 'abril',
        '05': 'mayo', '06': 'junio', '07': 'julio', '08': 'agosto',
        '09': 'septiembre', '10': 'octubre', '11': 'noviembre', '12': 'diciembre'
      };
      return `${year}, ${parseInt(day)} de ${monthNames[month]}`;
    } else if (month) {
      const monthNames = {
        '01': 'enero', '02': 'febrero', '03': 'marzo', '04': 'abril',
        '05': 'mayo', '06': 'junio', '07': 'julio', '08': 'agosto',
        '09': 'septiembre', '10': 'octubre', '11': 'noviembre', '12': 'diciembre'
      };
      return `${year}, ${monthNames[month]}`;
    }
    return year;
  };

  const shouldIncludeWebsiteName = () => {
    if (!formData.websiteName) return false;

    // Verificar si el nombre del sitio web es igual a algún autor
    const authorNames = authors.map(author => {
      if (author.type === 'institutional') {
        return author.name.toLowerCase();
      } else {
        return `${author.firstNames} ${author.lastNames}`.toLowerCase();
      }
    });

    return !authorNames.includes(formData.websiteName.toLowerCase());
  };

  const generatePreview = () => {
    // Verificar si hay autores institucionales con siglas
    const institutionalAuthors = authors.filter(
      author => author.type === 'institutional' && author.abbreviation
    );

    let narrativeCitation, parentheticalCitation, narrativeSubsequent, parentheticalSubsequent;
    const displayYear = formData.year === 's.f.' || formData.year === 's. f.' ? formData.year : formData.year;

    if (institutionalAuthors.length === 1 && (authorType === 'institutional' || authorType === 'both')) {
      // Para cita narrativa con institucional con sigla: "Nombre (SIGLA, año)"
      narrativeCitation = `${institutionalAuthors[0].name} (${institutionalAuthors[0].abbreviation}, ${displayYear})`;
      
      // Para cita parentética: "(Nombre [SIGLA], año)"
      parentheticalCitation = `(${institutionalAuthors[0].name} [${institutionalAuthors[0].abbreviation}], ${displayYear})`;
      
      // Para cita narrativa subsecuente: "SIGLA (año)"
      narrativeSubsequent = `${institutionalAuthors[0].abbreviation} (${displayYear})`;
      
      // Para cita parentética subsecuente: "(SIGLA, año)"
      parentheticalSubsequent = `(${institutionalAuthors[0].abbreviation}, ${displayYear})`;
    } else {
      // Caso normal sin autor institucional con sigla o múltiples autores
      const authorsForCitation = formatAuthorsForCitation(authors, authorType);
      const authorsForParenthetical = formatAuthorsForParenthetical(authors, authorType);
      const authorsForCitationSubsequent = formatAuthorsForCitation(authors, authorType, false);
      const authorsForParentheticalSubsequent = formatAuthorsForParenthetical(authors, authorType, false);

      narrativeCitation = `${authorsForCitation} (${displayYear})`;
      parentheticalCitation = `(${authorsForParenthetical}, ${displayYear})`;
      narrativeSubsequent = `${authorsForCitationSubsequent} (${displayYear})`;
      parentheticalSubsequent = `(${authorsForParentheticalSubsequent}, ${displayYear})`;
    }

    const citation = {
      narrative: narrativeCitation,
      parenthetical: parentheticalCitation,
      narrativeSubsequent: narrativeSubsequent,
      parentheticalSubsequent: parentheticalSubsequent
    };

    const authorsForReference = formatAuthorsForReference(authors, authorType);
    const dateFormatted = formatDate();
    const formattedUrl = formatURL(formData.url);

    let referenceText = `${authorsForReference} (${dateFormatted}). <em>${formData.title}</em>`;
    
    if (shouldIncludeWebsiteName()) {
      referenceText += `. ${formData.websiteName}`;
    }
    
    if (formattedUrl) {
      referenceText += `. ${formattedUrl}`;
    } else {
      referenceText += '.';
    }

    const reference = {
      html: referenceText,
      text: referenceText.replace(/<[^>]*>/g, '')
    };

    setLiveResult({ citation, reference });
  };

  const generateCitation = () => {
    if (authors.length === 0 || !formData.title || (!formData.year && formData.year !== 's.f.' && formData.year !== 's. f.')) {
      alert('Por favor completa todos los campos obligatorios');
      return;
    }

    // Verificar si hay autores institucionales con siglas
    const institutionalAuthors = authors.filter(
      author => author.type === 'institutional' && author.abbreviation
    );

    let narrativeCitation, parentheticalCitation, narrativeSubsequent, parentheticalSubsequent;
    const displayYear = formData.year === 's.f.' || formData.year === 's. f.' ? formData.year : formData.year;

    if (institutionalAuthors.length === 1 && (authorType === 'institutional' || authorType === 'both')) {
      // Para cita narrativa con institucional con sigla: "Nombre (SIGLA, año)"
      narrativeCitation = `${institutionalAuthors[0].name} (${institutionalAuthors[0].abbreviation}, ${displayYear})`;
      
      // Para cita parentética: "(Nombre [SIGLA], año)"
      parentheticalCitation = `(${institutionalAuthors[0].name} [${institutionalAuthors[0].abbreviation}], ${displayYear})`;
      
      // Para cita narrativa subsecuente: "SIGLA (año)"
      narrativeSubsequent = `${institutionalAuthors[0].abbreviation} (${displayYear})`;
      
      // Para cita parentética subsecuente: "(SIGLA, año)"
      parentheticalSubsequent = `(${institutionalAuthors[0].abbreviation}, ${displayYear})`;
    } else {
      // Caso normal sin autor institucional con sigla o múltiples autores
      const authorsForCitation = formatAuthorsForCitation(authors, authorType);
      const authorsForParenthetical = formatAuthorsForParenthetical(authors, authorType);
      const authorsForCitationSubsequent = formatAuthorsForCitation(authors, authorType, false);
      const authorsForParentheticalSubsequent = formatAuthorsForParenthetical(authors, authorType, false);

      narrativeCitation = `${authorsForCitation} (${displayYear})`;
      parentheticalCitation = `(${authorsForParenthetical}, ${displayYear})`;
      narrativeSubsequent = `${authorsForCitationSubsequent} (${displayYear})`;
      parentheticalSubsequent = `(${authorsForParentheticalSubsequent}, ${displayYear})`;
    }

    const citation = {
      narrative: narrativeCitation,
      parenthetical: parentheticalCitation,
      narrativeSubsequent: narrativeSubsequent,
      parentheticalSubsequent: parentheticalSubsequent
    };

    const authorsForReference = formatAuthorsForReference(authors, authorType);
    const dateFormatted = formatDate();
    const formattedUrl = formatURL(formData.url);

    let referenceText = `${authorsForReference} (${dateFormatted}). <em>${formData.title}</em>`;
    
    if (shouldIncludeWebsiteName()) {
      referenceText += `. ${formData.websiteName}`;
    }
    
    if (formattedUrl) {
      referenceText += `. ${formattedUrl}`;
    } else {
      referenceText += '.';
    }

    const reference = {
      html: referenceText,
      text: referenceText.replace(/<[^>]*>/g, '')
    };

    setResult({ citation, reference });
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 dark:text-gray-100">Sitio web</h1>
        <p className="text-gray-600 dark:text-gray-400">Sitios, portales y páginas web</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-6 dark:bg-gray-800 dark:border-gray-700">
          <AuthorFields
            authors={authors}
            setAuthors={setAuthors}
            authorType={authorType}
            setAuthorType={setAuthorType}
          />

          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              Título de la entrada *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              required
            />
          </div>

          <div>
            <label htmlFor="websiteName" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              Título del sitio web
            </label>
            <input
              type="text"
              id="websiteName"
              name="websiteName"
              value={formData.websiteName}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
            />
            <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
              Si es igual al nombre del autor, se omitirá en la referencia
            </p>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label htmlFor="year" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                Año *
              </label>
              <input
                type="text"
                id="year"
                name="year"
                value={formData.year}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                placeholder="2024 o s.f. o s. f."
                required
              />
              <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
                Usar "s.f." o "s. f." si no hay fecha
              </p>
            </div>
            <div>
              <label htmlFor="day" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                Día
              </label>
              <input
                type="number"
                id="day"
                name="day"
                value={formData.day}
                onChange={handleInputChange}
                min="1"
                max="31"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                disabled={formData.year === 's.f.' || formData.year === 's. f.'}
              />
            </div>
            <div>
              <label htmlFor="month" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                Mes
              </label>
              <select
                id="month"
                name="month"
                value={formData.month}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                disabled={formData.year === 's.f.' || formData.year === 's. f.'}
              >
                <option value="">Seleccionar</option>
                <option value="01">Enero</option>
                <option value="02">Febrero</option>
                <option value="03">Marzo</option>
                <option value="04">Abril</option>
                <option value="05">Mayo</option>
                <option value="06">Junio</option>
                <option value="07">Julio</option>
                <option value="08">Agosto</option>
                <option value="09">Septiembre</option>
                <option value="10">Octubre</option>
                <option value="11">Noviembre</option>
                <option value="12">Diciembre</option>
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="url" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              URL *
            </label>
            <input
              type="text"
              id="url"
              name="url"
              value={formData.url}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              placeholder="URL (con o sin https://)"
              required
            />
            <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
              Puede introducir una URL con o sin el prefijo https://
            </p>
          </div>

          <button
            type="button"
            onClick={generateCitation}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            Generar cita y referencia
          </button>
        </div>

        <div className="space-y-6">
          {!result && liveResult && (
            <CitationResult
              citation={liveResult.citation}
              reference={liveResult.reference}
              livePreview={true}
            />
          )}
          <CitationResult
            citation={result?.citation}
            reference={result?.reference}
          />
        </div>
      </div>
    </div>
  );
}

export default WebsiteForm;